import React from 'react'
import { Logger, NOOP_VOID } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'

import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

import {
    Breadcrumbs,
    classToFComponent,
    EditorContextBar,
    EditorView,
    FCClassContext,
    ModuleView,
    PageSubtitle,
    PageTitle
} from 'src/utils/views'

import ConfigurationTabsScope, { ConfigurationTabsOption } from '../scopes'
import BillingConfigView from './billing_view'
import MailTemplateListView from './email_template_list_view'
import EmailConfigView from './email_view'
import NotificationManagerView from './notifications_manager_view'

const LOG = Logger.get('MailConfig-VIEW')

const TabPanel = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flexGrow: 1
})

const useStyles = makeStyles()({
    tabs: {
        marginTop: 16,
        minHeight: 42,
        '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
            backgroundColor: 'var(--green)'
        },
        '& .MuiTab-root': {
            minHeight: 42,
            padding: '10px 14px 12px',
            textTransform: 'none',
            fontSize: 14,
            fontWeight: 650,
            color: '#4e5752',
            '&:hover': { color: 'var(--green-800)' }
        },
        '& .MuiTab-root.Mui-selected': { color: 'var(--green-800)' }
    }
})

const tabIdxByTabKey = new Map<number, ConfigurationTabsOption>()
const tabKeyByTabIdx = new Map<ConfigurationTabsOption, number>()

const TAB_LABELS: { key: ConfigurationTabsOption; label: string }[] = [
    { key: 'email', label: 'E-mail' },
    { key: 'email-template', label: 'Templates de e-mail' },
    { key: 'notifications', label: 'Notificações' },
    { key: 'billing', label: 'Faturamento' }
]

type ConfigurationsViewProps = {
    className?: string
    scope: ConfigurationTabsScope
}

class ConfigurationsClass implements FCClassContext<ConfigurationsViewProps> {
    // :: emissors

    readonly handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        const key = tabIdxByTabKey.get(newValue) ?? 'email'
        this.scope.onSelect(key).catch(LOG.caught)
    }

    // :: Fields
    scope!: ConfigurationTabsScope
    sytles!: ReturnType<typeof useStyles>

    // :: Methods

    onSyncState({ scope }: ConfigurationsViewProps) {
        initializeStatics()
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: ConfigurationsViewProps) {
        this.scope = bindUpdate(React, scope)
        const classes = this.sytles.classes

        const tabIdx = tabKeyByTabIdx.get(scope.active) ?? 0

        return (
            <ModuleView className={className}>
                <EditorView>
                    <EditorContextBar>
                        <div>
                            <Breadcrumbs>
                                Administração / <b>Configurações</b>
                            </Breadcrumbs>
                            <PageTitle>Configurações</PageTitle>
                            <PageSubtitle>
                                Defina o servidor de e-mail, os modelos de mensagem, as notificações enviadas aos
                                usuários e a tabela de preços do contrato padrão.
                            </PageSubtitle>
                        </div>
                        <Tabs
                            className={classes.tabs}
                            value={tabIdx}
                            onChange={this.handleTabChange}
                            variant="scrollable"
                            aria-label="Listagens"
                        >
                            {TAB_LABELS.map(({ key, label }) => (
                                <Tab key={key} label={label} {...a11yProps(tabKeyByTabIdx.get(key) ?? 0)} />
                            ))}
                        </Tabs>
                    </EditorContextBar>

                    <TabPanel role="tabpanel" id={`simple-tabpanel-${tabIdx}`} aria-labelledby={`simple-tab-${tabIdx}`}>
                        {scope.active === 'email' && <EmailConfigView scope={this.scope.emailConfig} />}
                        {scope.active === 'email-template' && <MailTemplateListView scope={this.scope.emailTemplate} />}
                        {scope.active === 'notifications' && (
                            <NotificationManagerView scope={this.scope.notificationTab} />
                        )}
                        {scope.active === 'billing' && <BillingConfigView scope={this.scope.billingConfig} />}
                    </TabPanel>
                </EditorView>
            </ModuleView>
        )
    }
}

export const ConfigurationsClassView = classToFComponent(ConfigurationsClass, React)
export default ConfigurationsClassView

let initializeStatics = () => {
    const add = (idx: number, key: ConfigurationTabsOption) => {
        tabIdxByTabKey.set(idx, key)
        tabKeyByTabIdx.set(key, idx)
    }
    add(0, 'email')
    add(1, 'email-template')
    add(2, 'notifications')
    add(3, 'billing')

    initializeStatics = NOOP_VOID
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
    }
}
